"use client";

import { CheckCircle2, Minus, Plus } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { t } from "@/i18n";

interface ProductCardProps {
  product: any;
  name: string;
  price: string;
  image: string;
  qty: number;
  maxQty: number;
  isOutOfStock: boolean;
  feasibility?: any;
  onAdd: (key: string, item: any) => void;
  onRemove: (key: string) => void;
  onUpdateQty: (key: string, qty: number) => void;
  accentColor?: string;
  onNoPlayer?: () => void;
  disabledReason?: string;
  isPurchased?: boolean;
}

function hexToRgb(hex: string): { r: number; g: number; b: number } {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
      }
    : { r: 89, g: 152, b: 216 };
}

function lightTint(hex: string, alpha = 0.08): string {
  const { r, g, b } = hexToRgb(hex);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function borderTint(hex: string): string {
  const { r, g, b } = hexToRgb(hex);
  return `rgba(${r}, ${g}, ${b}, 0.22)`;
}

const ProductCard = ({
  product,
  name,
  price,
  image,
  qty,
  maxQty,
  isOutOfStock,
  feasibility,
  onAdd,
  onRemove,
  onUpdateQty,
  accentColor = "#5998d8",
  onNoPlayer,
  disabledReason,
  isPurchased,
}: ProductCardProps) => {
  const isSelected = qty > 0;
  const isOwned = isPurchased === true;
  const cannotAdd = feasibility ? !feasibility.canAdd && qty > 0 : false;
  const isStockExhausted =
    !isSelected && feasibility && !feasibility.canAdd && maxQty <= 0;

  const isUnavailable =
    isOutOfStock || isStockExhausted || isOwned || !!disabledReason;

  const bgColor = lightTint(accentColor, 0.06);
  const borderColor = borderTint(accentColor);
  const selectedBg = lightTint(accentColor, 0.12);
  const selectedBorder = lightTint(accentColor, 0.45);

  const limitedOffer = product._offer;
  const offerPct = limitedOffer
    ? Math.round(
        ((limitedOffer.originalPrice - limitedOffer.discountedPrice) /
          limitedOffer.originalPrice) *
          100,
      )
    : 0;

  if (isUnavailable) {
    return (
      <div
        className="md:w-[200px] w-[165px] min-h-[217px] p-1 border inline-flex flex-col justify-between items-start opacity-60 pointer-events-none"
        style={{
          background: lightTint("#888", 0.06),
          borderColor: borderTint("#888"),
        }}
      >
        <div className="self-stretch h-[132px] flex flex-col justify-start items-center gap-2 opacity-30 grayscale relative cursor-not-allowed">
          {/* Limited Time Offer Badge (Unavailable) */}
          {limitedOffer && offerPct > 0 && (
            <div className="absolute top-0 right-0 z-20 grayscale opacity-60">
              <div className="bg-gradient-to-br from-blue-600 to-blue-800 text-white text-[9px] font-black px-2 py-1.5 rounded-bl-xl shadow-lg flex flex-col items-center leading-none">
                <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/10 to-blue-300/20" />
                <span className="relative z-10 mb-0.5 leading-tight tracking-wider">
                  LIMITED
                </span>
                <span className="relative z-10 text-[7px] uppercase tracking-[0.15em] font-bold text-blue-200">
                  TIME
                </span>
              </div>
            </div>
          )}
          {/* Visual Discount Badge (Unavailable) */}
          {!limitedOffer &&
            product.visualDiscountEnabled &&
            Number(product.visualDiscountPercentage || 0) > 0 && (
              <div className="absolute top-0 right-0 z-20 grayscale opacity-70">
                <div className="bg-red-600 text-white text-[9px] font-black px-2 py-1 rounded-bl shadow-md flex flex-col items-center leading-none">
                  <span className="mb-0.5">
                    {product.visualDiscountPercentage}%
                  </span>
                  <span className="text-[6px] opacity-90 uppercase tracking-tighter">
                    OFF
                  </span>
                </div>
              </div>
            )}
          <Image
            src={image}
            alt={name}
            fill
            sizes="(max-width: 768px) 165px, 200px"
            className="rounded-[3px] object-contain p-3"
          />
        </div>

        <div className="pl-1.5 flex flex-col justify-start items-start gap-1 py-1">
          <div
            className="font-mono text-[12px]   font-medium uppercase leading-3 line-clamp-2"
            style={{ color: "#9ca3af" }}
          >
            {name}
          </div>
          <div className="flex items-baseline gap-1.5 flex-wrap">
            {limitedOffer ? (
              <>
                <div className="font-mono text-[15px] font-black uppercase leading-tight tracking-tight shrink-0 text-blue-600 drop-shadow-[0_0_8px_rgba(37,99,235,0.3)]">
                  Rs.{Number(limitedOffer.discountedPrice).toLocaleString()}
                </div>
                <div className="font-mono text-[10px] line-through text-muted-foreground/40 leading-none antialiased shrink-0">
                  Rs.{Number(limitedOffer.originalPrice).toLocaleString()}
                </div>
              </>
            ) : (
              <>
                <div
                  className="font-mono text-[15px] font-medium uppercase leading-tight tracking-tight opacity-40 grayscale shrink-0"
                  style={{ color: "#9ca3af" }}
                >
                  {price}
                </div>
                {product.visualDiscountEnabled &&
                  Number(product.visualDiscountPercentage || 0) > 0 && (
                    <div className="font-mono text-[10px] line-through text-slate-500/40 leading-none antialiased shrink-0">
                      Rs.
                      {(
                        Number(
                          (product.websitePriceLkr ?? product.priceLkr) || 0,
                        ) *
                        (1 +
                          Number(product.visualDiscountPercentage || 0) / 100)
                      ).toFixed(0)}
                    </div>
                  )}
              </>
            )}
          </div>
        </div>
        <div className="self-stretch h-7 bg-gray-300 flex justify-center items-center">
          <span className="font-mono flex-1 text-center text-[#f9f9f9] text-[10px] font-semibold uppercase leading-3">
            {isOwned
              ? "Already Owned"
              : disabledReason
                ? disabledReason
                : isStockExhausted && feasibility?.reason
                  ? feasibility.reason
                  : "UNAVAILABLE"}
          </span>
        </div>
      </div>
    );
  }

  const isLevelUpPass = product.id.startsWith("lvl_");
  const isSpecial =
    product.isSpecialOffer ||
    product.category === "offers" ||
    product.id?.startsWith("offer_");
  const effectiveMax =
    isSpecial || isLevelUpPass ? Math.min(1, maxQty) : maxQty;

  const specialBg = "#FEFCE8";
  const specialBorder = "#FDE047";
  const specialSelectedBg = "#FEF08A";
  const specialSelectedBorder = "#EAB308";

  return (
    <div
      className={`md:w-[188px] w-[165px] min-h-[217px] p-1 border inline-flex flex-col justify-between items-start transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg group relative overflow-hidden ${
        isSelected ? "shadow-md" : ""
      } ${
        isSpecial
          ? "shadow-[0_0_12px_rgba(234,179,8,0.2)] hover:shadow-[0_0_18px_rgba(234,179,8,0.35)]"
          : ""
      }`}
      style={{
        background: isSpecial
          ? isSelected
            ? specialSelectedBg
            : specialBg
          : isSelected
            ? selectedBg
            : bgColor,
        borderColor: isSpecial
          ? isSelected
            ? specialSelectedBorder
            : specialBorder
          : isSelected
            ? selectedBorder
            : borderColor,
      }}
    >
      {/* Full Card Shine Sweep for Special Offers */}
      {isSpecial && <div className="animate-full-card-shine" />}

      <div
        className="self-stretch h-[132px] flex flex-col justify-start items-center gap-2 cursor-pointer relative overflow-hidden rounded-[3px] group z-10"
        onClick={() => {
          if (isSelected) return;
          if (onNoPlayer) {
            onNoPlayer();
            return;
          }
          const cappedQty = Math.min(1, maxQty);
          if (cappedQty <= 0) return;
          onAdd(product.id, {
            qty: cappedQty,
            item_id: product.item_id,
            isChannelItem: product.isChannelItem || false,
            priceLkr: product.priceLkr,
            websitePriceLkr: product.websitePriceLkr,
          });
        }}
      >
        {/* Limited Time Offer Badge */}
        {limitedOffer && offerPct > 0 && (
          <div className="absolute top-0 right-0 z-20 animate-in fade-in slide-in-from-right-2 duration-500">
            <div className="relative bg-gradient-to-br from-blue-600 to-blue-800 text-white text-[9px] font-black px-2 py-1.5 rounded-bl-xl shadow-lg flex flex-col items-center leading-none overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/10 to-blue-300/20" />
              <div className="absolute inset-0 animate-shimmer bg-gradient-to-r from-transparent via-white/20 to-transparent skew-x-[-20deg]" />
              <span className="relative z-10 mb-0.5 leading-tight tracking-wider">
                LIMITED
              </span>
              <span className="relative z-10 text-[7px] uppercase tracking-[0.15em] font-bold text-blue-200">
                OFFER
              </span>
            </div>
          </div>
        )}
        {/* Visual Discount Badge */}
        {!limitedOffer &&
          product.visualDiscountEnabled &&
          Number(product.visualDiscountPercentage || 0) > 0 && (
            <div className="absolute top-0 right-0 z-20">
              <div className="bg-red-600 text-white text-[9px] font-black px-2 py-1 rounded-bl shadow-lg animate-in fade-in slide-in-from-right-2 duration-500 flex flex-col items-center leading-none">
                <span className="mb-0.5">
                  {product.visualDiscountPercentage}%
                </span>
                <span className="text-[6px] opacity-90 uppercase tracking-tighter">
                  OFF
                </span>
              </div>
            </div>
          )}
        {/* Special Offer Badge */}
        {!limitedOffer &&
          (!product.visualDiscountEnabled ||
            !product.visualDiscountPercentage) &&
          isSpecial && (
            <div className="absolute top-0 right-0 z-20 animate-in fade-in slide-in-from-right-2 duration-500">
              <div className="relative bg-gradient-to-br from-amber-500 to-yellow-500 text-white text-[9px] font-black px-2 py-1.5 rounded-bl-xl shadow-lg flex flex-col items-center leading-none overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/30 to-yellow-200/40" />
                <div className="absolute inset-0 animate-shimmer bg-gradient-to-r from-transparent via-white/30 to-transparent skew-x-[-20deg]" />
                <span className="relative z-10 mb-0.5 leading-tight tracking-wider text-[8px]">
                  ONE TIME
                </span>
                <span className="relative z-10 text-[7px] uppercase tracking-[0.15em] font-bold text-amber-100">
                  OFFER
                </span>
              </div>
            </div>
          )}
        <Image
          src={image}
          fill
          sizes="(max-width: 768px) 250px, 188px"
          quality={50}
          className="rounded-[3px] object-contain transition-transform duration-500 group-hover:scale-105 p-3"
          alt={name}
        />
        {isSelected && (
          <div
            className="absolute top-1 left-1 w-5 h-5 rounded-full flex items-center justify-center shadow-md animate-in zoom-in duration-300"
            style={{ backgroundColor: accentColor }}
          >
            <CheckCircle2 className="w-3 h-3 text-white" />
          </div>
        )}
      </div>

      <div className="pl-1.5 flex flex-col justify-start items-start gap-1 py-1 w-full z-10">
        <div className="flex items-center justify-between w-full pr-1.5">
          <div
            className={`font-mono text-[10px] font-bold uppercase leading-3 line-clamp-2 ${isSpecial ? "text-black" : ""}`}
            style={{ color: isSpecial ? "#000000" : accentColor }}
          >
            {name}
          </div>
          <Link
            href={`/products/${product.slug || product.id}`}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`Details for ${name}`}
            className="text-[8px] font-sans font-bold bg-slate-950 hover:bg-slate-800 border border-slate-800 hover:border-slate-700 px-2 py-0.5 rounded text-slate-100 hover:text-white transition-all duration-300 shrink-0 ml-1.5 opacity-100 md:opacity-0 group-hover:opacity-100 shadow-md"
          >
            DETAILS
          </Link>
        </div>
        <div className="flex items-baseline gap-1.5 flex-wrap">
          {limitedOffer ? (
            <>
              <div
                className={`font-mono text-[15px] font-black uppercase leading-tight tracking-tight shrink-0 ${isSpecial ? "text-black" : "text-blue-600 drop-shadow-[0_0_8px_rgba(37,99,235,0.3)]"}`}
              >
                Rs.{Number(limitedOffer.discountedPrice).toLocaleString()}
              </div>
              <div
                className={`font-mono text-[10px] line-through leading-none antialiased shrink-0 ${isSpecial ? "text-neutral-600" : "text-muted-foreground/60"}`}
              >
                Rs.{Number(limitedOffer.originalPrice).toLocaleString()}
              </div>
            </>
          ) : (
            <>
              <div
                className={`font-mono text-[15px] font-bold uppercase leading-tight tracking-tight shrink-0 ${isSpecial ? "text-black" : ""}`}
                style={{ color: isSpecial ? "#000000" : accentColor }}
              >
                {price}
              </div>
              {product.visualDiscountEnabled &&
                Number(product.visualDiscountPercentage || 0) > 0 && (
                  <div
                    className={`font-mono text-[10px] line-through leading-none antialiased shrink-0 ${isSpecial ? "text-neutral-600" : "text-muted-foreground/60"}`}
                  >
                    Rs.
                    {(
                      Number(
                        (product.websitePriceLkr ?? product.priceLkr) || 0,
                      ) *
                      (1 + Number(product.visualDiscountPercentage || 0) / 100)
                    ).toFixed(0)}
                  </div>
                )}
            </>
          )}
        </div>

        {cannotAdd && !isOutOfStock && (
          <div className="mt-0.5">
            <span className="text-[8px] font-bold text-rose-500 bg-rose-50 border border-rose-100 px-1.5 py-px rounded uppercase tracking-wide">
              Max reached
            </span>
          </div>
        )}
      </div>

      {isSelected ? (
        <div className="self-stretch h-7 flex items-center justify-center gap-2.5 animate-in fade-in slide-in-from-bottom-1 duration-200">
          <button
            className="w-6 h-6 rounded-full border bg-white/80 flex items-center justify-center cursor-pointer transition-all hover:bg-white hover:scale-110"
            style={{
              borderColor: isSpecial ? "#EAB308" : lightTint(accentColor, 0.35),
              color: isSpecial ? "#78350F" : accentColor,
            }}
            aria-label="Decrease quantity"
            onClick={(e) => {
              e.stopPropagation();
              if (qty <= 1) {
                onRemove(product.id);
              } else {
                onUpdateQty(product.id, qty - 1);
              }
            }}
          >
            <Minus size={12} />
          </button>
          <span
            className="font-mono text-[13px] font-bold min-w-[20px] text-center"
            style={{ color: isSpecial ? "#78350F" : accentColor }}
          >
            {qty}
          </span>
          <button
            className="w-6 h-6 rounded-full border bg-white/80 flex items-center justify-center cursor-pointer transition-all hover:bg-white hover:scale-110 disabled:opacity-30 disabled:cursor-not-allowed"
            style={{
              borderColor: isSpecial ? "#EAB308" : lightTint(accentColor, 0.35),
              color: isSpecial ? "#78350F" : accentColor,
            }}
            aria-label="Increase quantity"
            disabled={
              qty >= effectiveMax || (feasibility ? !feasibility.canAdd : false)
            }
            onClick={(e) => {
              e.stopPropagation();
              if (qty >= effectiveMax) return;
              if (feasibility && !feasibility.canAdd) return;
              onUpdateQty(product.id, qty + 1);
            }}
          >
            <Plus size={12} />
          </button>
        </div>
      ) : (
        <button
          className="self-stretch h-7 flex justify-center items-center gap-2.5 border-none cursor-pointer transition-all hover:brightness-105 active:scale-[0.97]"
          style={{ backgroundColor: isSpecial ? "#FACC15" : accentColor }}
          onClick={() => {
            if (onNoPlayer) {
              onNoPlayer();
              return;
            }
            const cappedQty = Math.min(1, effectiveMax);
            if (cappedQty <= 0) return;
            onAdd(product.id, {
              qty: cappedQty,
              item_id: product.item_id,
              isChannelItem: product.isChannelItem || false,
              priceLkr: product.priceLkr,
              websitePriceLkr: product.websitePriceLkr,
            });
          }}
        >
          <span
            className={`font-mono flex-1 text-center text-[10px] font-bold uppercase leading-3 ${
              isSpecial ? "text-amber-950" : "text-[#f9f9f9]"
            }`}
          >
            {t("addToCart")}
          </span>
        </button>
      )}
    </div>
  );
};

export default ProductCard;
